import { APIRequestState, AssignmentDocumentsEndpoint } from 'constants/API'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Currency, ZERO_DECIMAL, supportedDocumentFileTypes } from 'constants/misc'
import { FileState, useFileAPI } from 'components/common/FileAPI'
import { GRAY_400, GRAY_900 } from 'constants/styling/theme'
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { bigFromDecimal, decimalFromBig, formatPrice } from 'utils/price'
import { buildFloorPlanCorrectionBilling } from 'utils/serialization/upsellPayloads'
import { useGalleryAssignment, useGalleryDeal } from 'components/pages/Gallery/_main/contexts'

import { AdditionalChangeUpsellPayment } from './AdditionalChangeUpsellPayment.component'
import { CorrectionReasonType } from 'models/messageThreads'
import { DocumentUploadType } from 'constants/documents'
import { FileAPIUpload } from 'components/common/FileAPIUpload'
import { IdentifiableProduct } from 'utils/serialization/productIdentifier'
import { Label } from 'components/common/Label'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { QueryStatus } from 'components/common/QueryStatus'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import { getSignedURLRetrieverSeq } from 'components/common/FileAPI/fileUtils'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useCreateFloorPlanCorrectionRequest } from 'dataQueries/messageThreads'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const AvailableCorrectionReasons = new Set<CorrectionReasonType>([
  CorrectionReasonType.SUBSEQUENT_CHANGE,
  CorrectionReasonType.WRONG_IMPLEMENTATION,
])

/** The surface a floor plan change request was raised from. */
export enum FloorPlanRequestSource {
  CLIENT_GALLERY = 'client_gallery',
  PROPERTY_DOCUMENTS = 'property_documents',
}

/** interface @Props for the ClientCreateFloorPlanRequestDrawer component. */
interface Props {
  /** Determines if the drawer is open. */
  isOpen: boolean
  /** The surface mounting the drawer. Each derives the corrected document from its own listing, so a half-rolled-out or broken listing is only attributable with this. */
  source: FloorPlanRequestSource
  /** External reference for the current selected document (file name). */
  externalReference: string
  /** Correction reference of the current selected document, as returned by the documents listing. Identifies the delivered unit the correction applies to; the file name cannot when the document is stored under a normalized name. */
  correctionReference?: string
  /** Function to call when the drawer is closed. */
  onClose: () => void
}

/** File API scope for the thread drawer upload */
const fp_thread_drawer_upload_scope = 'CREATE_FP_THREAD_DRAWER'

/**
 * @component
 * Drawer for creating a new request thread in the client gallery for FLOOR PLAN CERTIFICATION products.
 *
 * @example
 * <ClientCreateFloorPlanRequestDrawer isOpen={true} externalReference="originalFileName.pdf" onClose={() => setOpenDrawer(false)} />
 */
export const ClientCreateFloorPlanRequestDrawer: React.FC<Props> = ({
  isOpen,
  source,
  externalReference,
  correctionReference,
  onClose,
}) => {

  const { t } = useTranslation(['threads', 'actions', 'common', 'gallery_staging_flow'])
  const { roles } = useAuth0()

  const { userPropertyWorkspaceId } = useUserData()
  const { assignmentId } = useClientGallery()
  const { refetchTotalRequestsCount } = useGalleryDeal()
  const { product, upsellProduct } = useGalleryAssignment()
  const createFloorPlanCorrectionRequest = useCreateFloorPlanCorrectionRequest()
  const canUserOrder = useCanUserOrder()
  const { spawnErrorToast } = useSnackbar()

  // Filter available correction reasons based on canOrder
  const availableCorrectionReasons = useMemo(() => {
    if (canUserOrder) {
      return AvailableCorrectionReasons
    }
    // When canOrder is false, only allow WRONG_IMPLEMENTATION
    return new Set<CorrectionReasonType>([CorrectionReasonType.WRONG_IMPLEMENTATION])
  }, [canUserOrder])

  const [newThreadMessage, setNewThreadMessage] = useState('')
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false)
  const [correctionType, setCorrectionType] = useState<CorrectionReasonType | null>(null)
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState<boolean>(false)
  const pricePerAdditionalChange = useMemo(() => bigFromDecimal(upsellProduct?.feePrice.value || 0), [upsellProduct?.feePrice.value])

  // Parse the react query state for the upsell action request using APIRequestState
  const documentChangeRequestState: APIRequestState = useMemo(() => {
    if (createFloorPlanCorrectionRequest.isError) return APIRequestState.ERROR
    if (createFloorPlanCorrectionRequest.isPending) return APIRequestState.RUNNING
    if (createFloorPlanCorrectionRequest.isSuccess) return APIRequestState.OK
    return APIRequestState.BEFORE_START
  }, [createFloorPlanCorrectionRequest.isError, createFloorPlanCorrectionRequest.isPending, createFloorPlanCorrectionRequest.isSuccess])

  /** Initializes File API for the request drawer */
  const drawerThreadUpload = useFileAPI(fp_thread_drawer_upload_scope, {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => {
      return api.post(
        roles.isClient ? AssignmentDocumentsEndpoint.POST_UPLOAD_URL_WITH_WORKSPACE : AssignmentDocumentsEndpoint.POST_UPLOAD_URL,
        { assignmentId, workspaceId: userPropertyWorkspaceId },
        {
          filename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
          type: DocumentUploadType.INPUT,
        },
        true
      )
    }),
    deleteHandler: (fileEntry, api) => api.delete(
      roles.isClient ? AssignmentDocumentsEndpoint.DELETE_DOCUMENT_WITH_WORKSPACE : AssignmentDocumentsEndpoint.DELETE_DOCUMENT,
      { assignmentId, workspaceId: userPropertyWorkspaceId },
      true,
      {
        params: {
          type: DocumentUploadType.INPUT,
          filename: fileEntry.gcFilename,
        }
      }
    )
  })

  const isFileUploadSuccessful = useMemo(() => {
    return drawerThreadUpload.allFilesArray.length > 0 && drawerThreadUpload.allFilesArray.every((file) => file.state === FileState.SUCCESS)
  }, [drawerThreadUpload.allFilesArray])

  /** Handles clearing the input fields and purge files. */
  const handleClearDrawer = useCallback(() => {
    if (newThreadMessage !== '') setNewThreadMessage('')
    if (correctionType !== null) setCorrectionType(null)
    if (drawerThreadUpload.allFilesArray.length > 0) drawerThreadUpload.purgeFilesScope(fp_thread_drawer_upload_scope)
  }, [correctionType, drawerThreadUpload, newThreadMessage])

  /** Handles closing the drawer, clearing all the drawer data and resetting the query. */
  const handleCloseDrawer = useCallback(() => {
    handleClearDrawer()
    onClose()
    setIsUpsellModalOpen(false)
    // Reset the request query so the user can make another request
    createFloorPlanCorrectionRequest.reset()
  }, [createFloorPlanCorrectionRequest, handleClearDrawer, onClose])

  /** Handles the creation of a new request thread. */
  const handleCreateRequestThread = useCallback((paymentIntentId?: string, billingProduct?: IdentifiableProduct) => {

    if (!correctionType) return

    const newThreadFilenames = drawerThreadUpload.allFilesArray.map((file) => file.gcFilename).filter((filename): filename is string => !!filename)

    let billing

    try {
      billing = buildFloorPlanCorrectionBilling({
        billingProduct,
        paymentIntentId,
        correlation: { source, correctionType, paymentIntentId, assignmentId },
      })
    } catch {
      // The builder already logged which product carries no usable identifier. The client has just
      // entered payment details, so tell them the request was not sent and nothing was charged.
      spawnErrorToast(t('gallery_staging_flow:upsell_abort_error'))
      return
    }

    logAnalyticsEvent(AnalyticsEvent.CLICK_CHANGE_REQUEST_SENT, {
      assignmentId,
      productKind: product?.kind,
      productCategory: product?.category,
      correctionType,
      source,
      // Whether the document identified the delivered unit or only carried a file name - the reference itself is
      // never sent to a provider.
      hasCorrectionReference: !!correctionReference
    })

    createFloorPlanCorrectionRequest.mutate(
      {
        assignmentId,
        workspaceId: userPropertyWorkspaceId,
        correctionReference,
        fileNameToCorrect: externalReference,
        message: newThreadMessage,
        correctionTemplateFileName: newThreadFilenames[0],
        correctionReason: correctionType,
        ...billing,
      },
      {
        onSuccess: () => {
          handleClearDrawer()
          setTimeout(() => {
            handleCloseDrawer()
            refetchTotalRequestsCount()
          }, 3000)
        },
        onError: (error) => {
          logger.error('Floor plan correction request failed', error, {
            context: {
              component: 'ClientCreateFloorPlanRequestDrawer',
              action: 'createCorrectionRequest',
              metadata: {
                assignmentId,
                source,
                correctionReason: correctionType,
                hasCorrectionReference: !!correctionReference
              }
            }
          })
        }
      }
    )
  }, [correctionType, drawerThreadUpload.allFilesArray, assignmentId, product?.kind, product?.category, createFloorPlanCorrectionRequest, userPropertyWorkspaceId, source, correctionReference, externalReference, newThreadMessage, handleClearDrawer, handleCloseDrawer, refetchTotalRequestsCount, spawnErrorToast, t])

  const handleChangeCorrectionType = useCallback((type: CorrectionReasonType) => {
    setCorrectionType(type)
  }, [])

  // Disable the submit button if the title, message, correctionType are empty or no files are uploaded
  useEffect(() => {
    setDisabledSubmitButton(
      !newThreadMessage || !isFileUploadSuccessful || !correctionType
    )
  }, [newThreadMessage, isFileUploadSuccessful, correctionType])

  return (
    <Fragment>
      <ResponsiveDrawer
        backdrop="blurred"
        isOpen={isOpen}
        onClose={handleCloseDrawer}
        title={t('new_request_title')}
        actionsSlot={
          <Stack direction="row" gap={1}>

            <MUIButton type="secondaryBorder" onClick={handleCloseDrawer}>
              {t('actions:Cancel')}
            </MUIButton>
            <MUIButton
              disabled={
                disabledSubmitButton ||
                !createFloorPlanCorrectionRequest.isIdle
              }
              isLoading={createFloorPlanCorrectionRequest.isPending}
              onClick={() => {
                // If the correction type is SUBSEQUENT_CHANGE open the upsell modal 
                if (correctionType === CorrectionReasonType.SUBSEQUENT_CHANGE) {
                  setIsUpsellModalOpen(true)
                  return
                } else {
                  handleCreateRequestThread()
                }
              }}
            >
              {t('send_request')}
              {correctionType === CorrectionReasonType.SUBSEQUENT_CHANGE &&
                ` + ${formatPrice({
                  value: decimalFromBig(pricePerAdditionalChange),
                  currency:
                    upsellProduct?.feePrice.currency || Currency.EUR,
                  discountPercentage:
                    upsellProduct?.feePrice.discountPercentage || ZERO_DECIMAL,
                })}`
              }
            </MUIButton>

          </Stack>
        }
      >
        <Stack gap={2}>

          <SystemMessage
            message={t('create_thread.payment_info_message')}
          />

          <MUIDropdown
            button={(isOpen, action) => (
              <MUIDropdownInput
                required
                readOnly
                label={t('correction_type.label')}
                placeholder={t('correction_type.placeholder')}
                value={!!correctionType ? t(`correction_type.options.${correctionType}`) : ''}
                onClick={action}
              />
            )}
          >
            {Array.from(availableCorrectionReasons).map((type) => (
              <MUIDropdownItem key={type} onClick={() => handleChangeCorrectionType(type)}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                  {t(`correction_type.options.${type}`)}
                </Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>

          <MUIInputField
            required
            isMultiline={true}
            value={newThreadMessage}
            label={t('create_thread.label_message')}
            onChange={(e) => setNewThreadMessage(e.target.value)}
          />

          <Stack gap={0.6}>
            <Label text={t('create_thread.label_attachments')} required />

            <FileAPIUpload
              boxElevation="xs"
              borderBoxColor={GRAY_400}
              files={drawerThreadUpload.allFilesArray}
              maxNumberOfFiles={1}
              acceptedFileTypes={[...supportedDocumentFileTypes]} // Only PDF 
              uploadHandler={(acceptedFiles) => drawerThreadUpload.uploadFiles(acceptedFiles)}
              onDelete={(fileId) => drawerThreadUpload.deleteFiles([fileId])}
            />
          </Stack>

          <SystemMessage
            message={t('create_thread.upload_info_message')}
          />

        </Stack>

        <QueryStatus
          query={createFloorPlanCorrectionRequest}
          successMessage={t('create_thread.success')}
          spaceTopRem={2}
          onPurge={() => createFloorPlanCorrectionRequest.reset()}
        />
      </ResponsiveDrawer>

      {isUpsellModalOpen && upsellProduct &&
        <AdditionalChangeUpsellPayment
          successTitle={t('upsell_purchase_modal:thanks_for_request')}
          assignmentId={assignmentId}
          product={upsellProduct}
          additionalChangeMutation={createFloorPlanCorrectionRequest}
          additionalChangeRequestState={documentChangeRequestState}
          pricePerAdditionalChange={pricePerAdditionalChange}
          isAdditionalChangePurchaseModalOpen={isUpsellModalOpen}
          setAdditionalChangePurchaseModalOpen={setIsUpsellModalOpen}
          submitAdditionalChangeRequest={handleCreateRequestThread}
        />
      }

    </Fragment>
  )
}
